import RequestForm from "@components/helpCenter/RequestForm"
import { getWebAccessEndDate } from "@helpers/helpCenterResult"
import { CancelationCompletedPageLocationState } from "@pages/helpCenter/CancelationCompletedPage"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useLoaderData, useNavigate } from "react-router-dom"
import CancelMultipleSubscriptionsDialog from "../../components/helpCenter/CancelMultipleSubscriptionsDialog"
import { cancelRevenueCatSubscriptions } from "../../helpers/supabase"
import { GetSubscriptionsLoaderData } from "../../loaders/getSubscriptionsLoader"

export type CancelationType = "all" | "web"

export default function CancelationPage() {
  const [displayDialog, setDisplayDialog] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState<CancelationType | null>(null)

  const { cancelableSubscriptions, userEmail } =
    useLoaderData() as GetSubscriptionsLoaderData

  const navigate = useNavigate()
  const { t } = useTranslation()

  const hasMultipleActiveSubscriptions = cancelableSubscriptions.length > 1

  const hasNoActiveSubscriptions = cancelableSubscriptions.length === 0

  const cancelSubscriptions = async (cancelationType: CancelationType) => {
    const {
      activeAppStoreSubscriptionsCount,
      activePlayStoreSubscriptionsCount,
      activeStripeSubscriptionsCount,
      canceledStripeSubscriptionsCount,
    } = await cancelRevenueCatSubscriptions()

    const state: CancelationCompletedPageLocationState = {
      // NOTE: Web subscriptions are canceled at period end: the access ends
      // when they would have renewed.
      accessEndDate: getWebAccessEndDate(cancelableSubscriptions),
      activeAppStoreSubscriptionsCount,
      activePlayStoreSubscriptionsCount,
      activeStripeSubscriptionsCount,
      canceledStripeSubscriptionsCount,
      cancelationType,
    }

    navigate("/help-center/cancelation/completed", { replace: true, state })
  }

  const handleClick = async () => {
    setIsSubmitting("all")

    if (hasMultipleActiveSubscriptions) {
      setDisplayDialog(true)
    } else {
      await cancelSubscriptions("all")
    }

    setIsSubmitting(null)
  }

  return (
    <>
      <RequestForm
        buttonTitle={t("pages.helpCenter.cancelation.requestCancelation")}
        email={userEmail}
        hasNoSubscription={hasNoActiveSubscriptions}
        heading={t("pages.helpCenter.cancelation.heading")}
        onSubmit={() => void handleClick()}
        subject={t("pages.helpCenter.cancelSubscription")}
        submitting={!!isSubmitting}
      />

      {displayDialog && (
        <CancelMultipleSubscriptionsDialog
          cancelableSubscriptions={cancelableSubscriptions}
          cancelSubscriptions={cancelSubscriptions}
          isSubmitting={isSubmitting}
          onClose={() => setDisplayDialog(false)}
          open
          setIsSubmitting={setIsSubmitting}
        />
      )}
    </>
  )
}
